/**
 * Browser Uptime Tracker for RUM
 *
 * Tracks browser uptime sessions and connectivity metrics
 * Records sessions dynamically when browser opens/closes (detected by time gaps)
 * Uses IndexedDB for persistence across sessions
 * Pushes metrics to Faro every 24 hours
 */

import { faro } from './rum-utility'
import { VoyageImoModel } from '../models/voyage-imo-model'

const DB_NAME = 'BrowserUptimeDB'
const STORE_NAME = 'uptime-metrics'
const DB_VERSION = 1

interface UptimeSession {
    id: string // 'session-{timestamp}'
    imo: string
    sessionStart: number
    sessionEnd: number
    lastOnlineTimestamp: number | null // Last time vessel was confirmed online
    totalOnlineDuration: number // Total milliseconds online during this session
}

interface UptimeGlobalTiming {
    id: string // 'global-timing'
    lastResetTimestamp: number
    lastPushTimestamp: number | null
}

interface ActiveSession {
    sessionStart: number
    totalOnlineDuration: number
    lastOnlineTimestamp: number | null
    lastCheckTimestamp: number
}

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes - How often we save session data
const HEARTBEAT_INTERVAL = 5 * 60 * 1000 // 5 minutes - How often heartbeat runs (from voyage-topbar-cloud.ts)
const ONLINE_THRESHOLD = 2 * HEARTBEAT_INTERVAL // 10 minutes - If no heartbeat in 10min, consider offline
const SESSION_GAP_THRESHOLD = 2 * SESSION_CHECK_INTERVAL // 10 minutes - If gap > 10min, browser was closed
const TRACKING_INTERVAL = 24 * 60 * 60 * 1000 // 24 hours
const DAILY_PUSH_CHECK_INTERVAL = 60 * 60 * 1000 // 1 hour
const GLOBAL_TIMING_ID = 'uptime-global-timing'

class BrowserUptimeTracker {
    private db: IDBDatabase | null = null
    private checkIntervalId: number | null = null
    private pushIntervalId: number | null = null
    private isVesselOnCloud: boolean
    private lastOnlineTimestamp: number | null = null
    private activeSession: ActiveSession | null = null

    constructor() {
        this.isVesselOnCloud = (window as any).appenv?.vesselOnCloud ?? false
    }

    async initialize(): Promise<void> {
        if (!this.isVesselOnCloud) {
            return
        }

        try {
            await this.openDatabase()
            await this.ensureGlobalTimingExists()
            await this.checkForPreviousSession()
            this.startCheckInterval()
            this.startDailyPushInterval()

            await this.checkAndPushIfNeeded()

        } catch (error) {
            console.error('Failed to initialize browser uptime tracker:', error)
        }
    }

    private openDatabase(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION)

            request.onerror = () => {
                reject(new Error('Failed to open IndexedDB for uptime tracking'))
            }

            request.onsuccess = () => {
                this.db = request.result
                resolve()
            }

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' })
                }
            }
        })
    }

    private async ensureGlobalTimingExists(): Promise<void> {
        const timing = await this.getGlobalTiming()

        if (!timing) {
            const initialTiming: UptimeGlobalTiming = {
                id: GLOBAL_TIMING_ID,
                lastResetTimestamp: Date.now(),
                lastPushTimestamp: null,
            }
            await this.saveGlobalTiming(initialTiming)
        }
    }

    private getGlobalTiming(): Promise<UptimeGlobalTiming | null> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readonly')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.get(GLOBAL_TIMING_ID)

            request.onsuccess = () => {
                resolve(request.result || null)
            }

            request.onerror = () => {
                reject(new Error('Failed to get global timing'))
            }
        })
    }

    private saveGlobalTiming(timing: UptimeGlobalTiming): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readwrite')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.put(timing)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to save global timing'))
            }
        })
    }

    /**
     * Check if there was a previous unclosed session from last time browser was open
     */
    private async checkForPreviousSession(): Promise<void> {
        try {
            const allSessions = await this.getAllSessions()
            if (allSessions.length === 0) {
                // No previous session, start a new one
                this.startNewSession()
                return
            }

            // Get the most recent session
            const lastSession = allSessions.reduce((latest, session) =>
                session.sessionStart > latest.sessionStart ? session : latest
            )

            const now = Date.now()
            const timeSinceLastSession = now - lastSession.sessionEnd

            if (timeSinceLastSession > SESSION_GAP_THRESHOLD) {
                // Browser was closed, last session is complete, start new one
                console.log(`Browser was closed. Last session ended ${Math.floor(timeSinceLastSession / 1000)}s ago. Starting new session.`)
                this.startNewSession()
            } else {
                // Browser is continuing from previous session (likely a refresh)
                this.activeSession = {
                    sessionStart: lastSession.sessionStart,
                    totalOnlineDuration: lastSession.totalOnlineDuration,
                    lastOnlineTimestamp: lastSession.lastOnlineTimestamp,
                    lastCheckTimestamp: now,
                }
                // restore lastOnlineTimestamp from saved session
                this.lastOnlineTimestamp = lastSession.lastOnlineTimestamp
            }
        } catch (error) {
            console.error('Failed to check for previous session:', error)
            this.startNewSession()
        }
    }

    /**
     * Start a new browser session
     */
    private startNewSession(): void {
        const now = Date.now()
        this.activeSession = {
            sessionStart: now,
            totalOnlineDuration: 0,
            lastOnlineTimestamp: this.lastOnlineTimestamp,
            lastCheckTimestamp: now,
        }
    }

    /**
     * Update the last online timestamp
     * Called from sendHeartBeat when status === 'Updated'
     */
    updateLastOnlineTimestamp(): void {
        this.lastOnlineTimestamp = Date.now()

        if (!this.activeSession) {
            this.startNewSession()
        }

        if (this.activeSession) {
            this.activeSession.lastOnlineTimestamp = this.lastOnlineTimestamp
        }
    }

    /**
     * Check interval - runs every 5minutes
     * Updates online duration based on last online timestamp
     */
    private async checkSession(): Promise<void> {
        if (!this.activeSession) {
            this.startNewSession()
            return
        }

        const now = Date.now()
        const timeSinceLastCheck = now - this.activeSession.lastCheckTimestamp

        // Determine if we were online during this interval
        // We're online if we received a heartbeat within the last threshold (2x heartbeat interval)
        if (this.lastOnlineTimestamp !== null) {
            const timeSinceLastOnline = now - this.lastOnlineTimestamp

            if (timeSinceLastOnline <= ONLINE_THRESHOLD) {
                // We're currently online - add the full interval duration
                this.activeSession.totalOnlineDuration += timeSinceLastCheck
            } // else offline and don't do anything
        } 

        this.activeSession.lastCheckTimestamp = now

        // Save the current active session (it will be overwritten on next check or finalized when closed)
        await this.saveCurrentSession()
    }

    /**
     * Save the current active session to IndexedDB
     */
    private async saveCurrentSession(): Promise<void> {
        if (!this.activeSession || !this.isVesselOnCloud || !this.db) {
            return
        }

        try {
            const currentImo = VoyageImoModel?.imo
            if (!currentImo) {
                console.warn('No IMO available, skipping session save')
                return
            }

            const now = Date.now()

            // Calculate the online duration including the current partial interval
            let totalOnlineDurationWithCurrentInterval = this.activeSession.totalOnlineDuration

            // If we're currently online, add the time since last check
            if (this.lastOnlineTimestamp !== null) {
                const timeSinceLastOnline = now - this.lastOnlineTimestamp
                if (timeSinceLastOnline <= ONLINE_THRESHOLD) {
                    const timeSinceLastCheck = now - this.activeSession.lastCheckTimestamp
                    totalOnlineDurationWithCurrentInterval += timeSinceLastCheck
                }
            }

            const session: UptimeSession = {
                id: `session-${this.activeSession.sessionStart}`,
                imo: currentImo.toString(),
                sessionStart: this.activeSession.sessionStart,
                sessionEnd: now,
                lastOnlineTimestamp: this.activeSession.lastOnlineTimestamp,
                totalOnlineDuration: totalOnlineDurationWithCurrentInterval,
            }

            await this.saveSession(session)
        } catch (error) {
            console.error('Failed to save current session:', error)
        }
    }

    private saveSession(session: UptimeSession): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readwrite')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.put(session)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to save session'))
            }
        })
    }

    private getAllSessions(): Promise<UptimeSession[]> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readonly')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.getAll()

            request.onsuccess = () => {
                const allRecords = request.result || []
                // Filter out global timing record, only return session records
                const sessions = allRecords.filter(
                    (record: any) => record.id && record.id.startsWith('session-'),
                ) as UptimeSession[]
                resolve(sessions)
            }

            request.onerror = () => {
                reject(new Error('Failed to get all sessions'))
            }
        })
    }

    /**
     * Format duration in hours, minutes, and seconds
     */
    private formatDuration(durationMs: number): string {
        const totalSeconds = Math.floor(durationMs / 1000)
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        return `${String(hours).padStart(2, '0')}hrs ${String(minutes).padStart(2, '0')}mins ${String(seconds).padStart(2, '0')}secs`
    }

    /**
     * Format timestamp to HH:MM:SS in UTC
     */
    private formatTimestamp(timestamp: number): string {
        const date = new Date(timestamp)
        const hours = String(date.getUTCHours()).padStart(2, '0')
        const minutes = String(date.getUTCMinutes()).padStart(2, '0')
        const seconds = String(date.getUTCSeconds()).padStart(2, '0')

        return `${hours}:${minutes}:${seconds}`
    }

    private async checkAndPushIfNeeded(): Promise<void> {
        try {
            const timing = await this.getGlobalTiming()

            if (!timing) {
                return
            }

            const timeSinceReset = Date.now() - timing.lastResetTimestamp

            // If 24 hours have passed since last reset, push and reset
            if (timeSinceReset >= TRACKING_INTERVAL) {
                await this.pushMetricsToRUM(timing)

                // Reset timing
                timing.lastResetTimestamp = Date.now()
                timing.lastPushTimestamp = Date.now()
                await this.saveGlobalTiming(timing)

                // Clear all sessions after successful push
                await this.clearAllSessions()

                // Restart the current session
                this.startNewSession()
            }
        } catch (error) {
            console.error('Failed to check and push uptime metrics:', error)
        }
    }

    private async pushMetricsToRUM(timing: UptimeGlobalTiming): Promise<void> {
        try {
            const allSessions = await this.getAllSessions()

            if (allSessions.length === 0) {
                return
            }

            // Filter sessions to only include those that started within the tracking period
            // This excludes sessions that started after the period ended (they belong to next period)
            const trackingPeriodEnd = timing.lastResetTimestamp + TRACKING_INTERVAL
            const sessionsInPeriod = allSessions.filter(session =>
                session.sessionStart <= trackingPeriodEnd
            )

            if (sessionsInPeriod.length === 0) {
                return
            }

            // Calculate total summary from sessions in period (capped at tracking period end)
            const totalBrowserOpenDuration = sessionsInPeriod.reduce((sum, session) => {
                const cappedEnd = Math.min(session.sessionEnd, trackingPeriodEnd)
                return sum + (cappedEnd - session.sessionStart)
            }, 0)
            const totalOnlineDuration = sessionsInPeriod.reduce((sum, session) => {
                const cappedEnd = Math.min(session.sessionEnd, trackingPeriodEnd)
                const cappedDuration = cappedEnd - session.sessionStart
                const fullDuration = session.sessionEnd - session.sessionStart
                const cappedOnline = fullDuration > 0
                    ? (session.totalOnlineDuration * cappedDuration) / fullDuration
                    : session.totalOnlineDuration
                return sum + cappedOnline
            }, 0)
            let totalOnlinePercentage = totalBrowserOpenDuration > 0
                ? (totalOnlineDuration / totalBrowserOpenDuration) * 100
                : 0

            // Round to 100% if very close (accounts for timing precision)
            if (totalOnlinePercentage >= 99.5) {
                totalOnlinePercentage = 100
            }

            // Build event data with individual session entries
            const eventData: any = {
                trackingPeriodStart: `${new Date(timing.lastResetTimestamp).toISOString()}`,
                trackingPeriodEnd: `${new Date(trackingPeriodEnd).toISOString()}`,
                trackingDuration: this.formatDuration(TRACKING_INTERVAL),
                totalSessions: `${sessionsInPeriod.length}`,
                totalBrowserOpenDuration: this.formatDuration(totalBrowserOpenDuration),
                totalOnlineDuration: this.formatDuration(totalOnlineDuration),
                totalOnlinePercentage: `${totalOnlinePercentage.toFixed(1)}%`,
            }

            // adding individual session entries (only those in the tracking period)
            sessionsInPeriod.forEach((session, index) => {
                // cap session end time at tracking period end to avoid exceeding tracking duration
                const cappedSessionEnd = Math.min(session.sessionEnd, trackingPeriodEnd)
                const cappedSessionDuration = cappedSessionEnd - session.sessionStart

                // proportionally adjust online duration if session was capped
                const sessionDuration = session.sessionEnd - session.sessionStart
                const cappedOnlineDuration = sessionDuration > 0
                    ? (session.totalOnlineDuration * cappedSessionDuration) / sessionDuration
                    : session.totalOnlineDuration

                const startTimeStr = this.formatTimestamp(session.sessionStart)
                const endTimeStr = this.formatTimestamp(cappedSessionEnd)
                const durationStr = this.formatDuration(cappedSessionDuration)
                const onlineDurationStr = this.formatDuration(cappedOnlineDuration)
                const offlineDurationStr = this.formatDuration(cappedSessionDuration - cappedOnlineDuration)
                let onlinePercentage = cappedSessionDuration > 0
                    ? (cappedOnlineDuration / cappedSessionDuration) * 100
                    : 0

                // Round to 100% if very close (accounts for timing precision)
                if (onlinePercentage >= 99.5) {
                    onlinePercentage = 100
                }

                eventData[`session${index + 1}`] = `${startTimeStr} - ${endTimeStr} | Duration: ${durationStr} | Connected: ${onlineDurationStr} (${onlinePercentage.toFixed(1)}%) | Disconnected: ${offlineDurationStr}`
            })

            faro.api.pushEvent('daily-browser-uptime', eventData)
            console.log('Browser uptime metrics pushed to Faro:', eventData)
        } catch (error) {
            console.error('Failed to push uptime metrics to RUM:', error)
        }
    }

    private async clearAllSessions(): Promise<void> {
        if (!this.db) {
            return
        }

        const transaction = this.db.transaction(STORE_NAME, 'readwrite')
        const store = transaction.objectStore(STORE_NAME)

        const getAllKeysRequest = store.getAllKeys()

        return new Promise((resolve, reject) => {
            getAllKeysRequest.onsuccess = () => {
                const keys = getAllKeysRequest.result

                // Delete all session records (but not global timing)
                const deletePromises: Promise<void>[] = []

                for (const key of keys) {
                    if (typeof key === 'string' && key.startsWith('session-')) {
                        deletePromises.push(
                            new Promise<void>((delResolve, delReject) => {
                                const deleteRequest = store.delete(key)
                                deleteRequest.onsuccess = () => delResolve()
                                deleteRequest.onerror = () =>
                                    delReject(
                                        new Error(`Failed to delete ${key}`),
                                    )
                            }),
                        )
                    }
                }

                Promise.all(deletePromises)
                    .then(() => resolve())
                    .catch(reject)
            }

            getAllKeysRequest.onerror = () => {
                reject(new Error('Failed to get all keys for clearing'))
            }
        })
    }

    /**
     * Start interval to check session status
     * Checks every 5 minutes
     */
    private startCheckInterval(): void {
        this.checkIntervalId = window.setInterval(async () => {
            await this.checkSession()
        }, SESSION_CHECK_INTERVAL)

        // Check immediately on start
        this.checkSession()
    }

    /**
     * Start interval to check and push metrics periodically
     * For testing: checks frequently based on TRACKING_INTERVAL
     * For production (24h): checks every hour
     */
    private startDailyPushInterval(): void {
        const checkInterval = DAILY_PUSH_CHECK_INTERVAL

        this.pushIntervalId = window.setInterval(async () => {
            await this.checkAndPushIfNeeded()
        }, checkInterval)
    }

    stop(): void {
        // Save the final session before stopping
        if (this.activeSession) {
            this.saveCurrentSession()
        }

        if (this.checkIntervalId !== null) {
            clearInterval(this.checkIntervalId)
            this.checkIntervalId = null
        }

        if (this.pushIntervalId !== null) {
            clearInterval(this.pushIntervalId)
            this.pushIntervalId = null
        }

        if (this.db) {
            this.db.close()
            this.db = null
        }
    }
}

export const browserUptimeTracker = new BrowserUptimeTracker()
